import { useNuxtApp } from '#app';

type LandingPageDependencyTiming = {
  url: string;
  label: string;
  kind: 'internal' | 'external';
  status?: number;
  durationMs: number;
  ok: boolean;
  errorMessage?: string;
};

type LandingPageSsrTiming = {
  dependencyTimings?: LandingPageDependencyTiming[];
};

const now = () => globalThis.performance?.now?.() ?? Date.now();

const toKind = (url: string): 'internal' | 'external' => {
  if (url.startsWith('/')) return 'internal';
  if (url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')) return 'internal';
  if (url.startsWith('https://veeso.ai') || url.startsWith('https://www.veeso.ai'))
    return 'internal';
  return 'external';
};

export async function withSsrDependencyTiming<T>(
  label: string,
  run: () => Promise<T>,
  meta: { url?: string; kind?: 'internal' | 'external'; status?: number } = {}
): Promise<T> {
  if (import.meta.client) {
    return run();
  }

  const nuxtApp = useNuxtApp();
  const event = nuxtApp.ssrContext?.event;
  const url = meta.url || label;
  const kind = meta.kind || toKind(url);
  const timing = (
    event?.context as {
      landingPageSsrTiming?: LandingPageSsrTiming;
    }
  )?.landingPageSsrTiming;
  const startedAt = now();
  const logDependency = (payload: {
    durationMs: number;
    ok: boolean;
    status?: number;
    errorMessage?: string;
  }) => {
    console.info('[landing-page][ssr][dependency]', {
      path: event?.path,
      ssrUrl: nuxtApp.ssrContext?.url || event?.path,
      label,
      url,
      kind,
      durationMs: Number(payload.durationMs.toFixed(1)),
      ok: payload.ok,
      status: payload.status,
      trackedInSummary: Boolean(timing),
      errorMessage: payload.errorMessage,
    });
  };

  try {
    const result = await run();
    const durationMs = now() - startedAt;
    timing?.dependencyTimings?.push({
      url,
      label,
      kind,
      status: meta.status,
      durationMs,
      ok: true,
    });
    logDependency({
      durationMs,
      ok: true,
      status: meta.status,
    });
    return result;
  } catch (error) {
    const durationMs = now() - startedAt;
    const errorMessage = error instanceof Error ? error.message : String(error);
    timing?.dependencyTimings?.push({
      url,
      label,
      kind,
      status: meta.status,
      durationMs,
      ok: false,
      errorMessage,
    });
    logDependency({
      durationMs,
      ok: false,
      status: meta.status,
      errorMessage,
    });
    throw error;
  }
}
